import { Box, IconButton, Tooltip } from "@mui/material";
import { formatCurrency, Icon, NULL_DISPLAY } from "@platform/design-system";

import { parseColId } from "../buildPatchDiff";

import type { FinancialsGridContext, FinancialsRow } from "../types";
import type { ICellRendererParams } from "ag-grid-community";

/**
 * A coverage's annual premium. A premium that an analyst typed over the value
 * calculated from the Quotes rates shows the pencil the Quotes grid puts on an
 * edited cell, beside a button that reverts it to the calculated value.
 */
export const PremiumCellRenderer = (
  params: ICellRendererParams<
    FinancialsRow,
    number | null,
    FinancialsGridContext
  >,
): React.JSX.Element => {
  const value = params.value ?? null;
  const premium = (
    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
      {value === null ? NULL_DISPLAY : formatCurrency(value)}
    </Box>
  );

  const column = parseColId(params.column?.getColId() ?? "");
  const coverageId = params.data
    ? params.context.coverageByRowId.get(params.data.rowId)
    : undefined;
  if (!column || coverageId === undefined) return premium;

  const { payload } = params.context;
  const cells =
    column.kind === "period"
      ? payload.periods[column.period].coverages
      : params.context.carrierById.get(column.carrierId)?.coverages;
  const cell = cells?.[coverageId];
  const overridden =
    cell !== undefined &&
    "premiumOverridden" in cell &&
    cell.premiumOverridden === true;
  if (!overridden) return premium;

  // A period column is too narrow for the premium and both icons on one line,
  // so the icons wrap below the premium rather than the premium wrapping.
  return (
    <Box
      data-testid="financials-premium-overridden"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "flex-end",
        columnGap: 0.5,
      }}
    >
      {premium}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title="Edited: this premium was typed over the calculated value">
          <span>
            <Icon name="edited" color="muted" />
          </span>
        </Tooltip>
        {params.context.premiumOverridesEnabled && (
          <Tooltip title="Revert to calculated value">
            <IconButton
              size="small"
              aria-label="Revert to calculated value"
              onClick={(event) => {
                // The click would otherwise start editing the cell.
                event.stopPropagation();
                params.context.revertPremium(coverageId, column);
              }}
              sx={{ p: 0.25 }}
            >
              <Icon name="revert" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
